import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { partnerNewsKeys } from '@/api/query-keys';
import { uploadToPresignedUrl } from '@/lib/file-utils';
import {
  confirmPartnerNewsImageUploadContract,
  createPartnerNewsContract,
  deletePartnerNewsContract,
  deletePartnerNewsImageContract,
  requestPartnerNewsImageUploadContract,
  updatePartnerNewsContract,
  type ConfirmPartnerNewsImageUploadResponse,
  type CreatePartnerNewsRequest,
  type CreatePartnerNewsResponse,
  type DeletePartnerNewsImageResponse,
  type DeletePartnerNewsResponse,
  type PartnerNewsImageFormat,
  type PartnerNewsImageSlot,
  type RequestPartnerNewsImageUploadRequest,
  type RequestPartnerNewsImageUploadResponse,
  type UpdatePartnerNewsRequest,
  type UpdatePartnerNewsResponse
} from '@bliks-io/contracts';

function useInvalidateNews() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: partnerNewsKeys.all });
}

export function useCreatePartnerNews() {
  const invalidate = useInvalidateNews();
  return useMutation({
    mutationFn: async (data: CreatePartnerNewsRequest) => {
      const { data: response } = await apiClient.post<CreatePartnerNewsResponse>(
        createPartnerNewsContract.path,
        data
      );
      return response.data;
    },
    onSuccess: invalidate
  });
}

export function useUpdatePartnerNews() {
  const invalidate = useInvalidateNews();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePartnerNewsRequest }) => {
      const { data: response } = await apiClient.patch<UpdatePartnerNewsResponse>(
        updatePartnerNewsContract.path.replace(':id', id),
        data
      );
      return response.data;
    },
    onSuccess: invalidate
  });
}

const imagePath = (path: string, id: string, slot: PartnerNewsImageSlot) =>
  path.replace(':id', id).replace(':slot', slot);

/** Presign → direct upload to storage → confirm, for the logo or the image of a news entry. */
export function useUploadPartnerNewsImage() {
  const invalidate = useInvalidateNews();
  return useMutation({
    mutationFn: async ({
      id,
      slot,
      format,
      file
    }: {
      id: string;
      slot: PartnerNewsImageSlot;
      format: PartnerNewsImageFormat;
      file: File;
    }) => {
      const { data: presigned } = await apiClient.post<RequestPartnerNewsImageUploadResponse>(
        imagePath(requestPartnerNewsImageUploadContract.path, id, slot),
        { format } satisfies RequestPartnerNewsImageUploadRequest
      );
      await uploadToPresignedUrl(presigned.url, presigned.fields, file);
      const { data: response } = await apiClient.post<ConfirmPartnerNewsImageUploadResponse>(
        imagePath(confirmPartnerNewsImageUploadContract.path, id, slot),
        { key: presigned.key, fileName: file.name }
      );
      return response.data;
    },
    onSuccess: invalidate
  });
}

export function useRemovePartnerNewsImage() {
  const invalidate = useInvalidateNews();
  return useMutation({
    mutationFn: async ({ id, slot }: { id: string; slot: PartnerNewsImageSlot }) => {
      const { data } = await apiClient.delete<DeletePartnerNewsImageResponse>(
        imagePath(deletePartnerNewsImageContract.path, id, slot)
      );
      return data.data;
    },
    onSuccess: invalidate
  });
}

export function useDeletePartnerNews() {
  const invalidate = useInvalidateNews();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<DeletePartnerNewsResponse>(
        deletePartnerNewsContract.path.replace(':id', id)
      );
      return data;
    },
    onSuccess: invalidate
  });
}
